/**
 * 例外の「種類名」を、ログに出してよい固定の文字列へ正規化する（2026-09-11・Codex レビュー MEDIUM）。
 *
 * ## なぜ要るか
 *
 * 例外本文（message / stack / cause）を外して `error.name` だけをログに出す箇所がある。
 * しかし name は書き換え可能な文字列で、`error.name = '山田 太郎'` のような例外では個人情報がそのまま残る。
 * ここで許可リストに載っている種類名だけを返し、それ以外はすべて 'UnknownError' に倒す。
 *
 * ## 許可リストの根拠
 *
 * - JavaScript 組み込みの例外型: Error / TypeError / RangeError / SyntaxError / ReferenceError / EvalError / URIError / AggregateError
 * - コード内で実際に使われている型名（2026-09-11 に rg で確認）:
 *   DrizzleQueryError / AbortError / PostgresError / TimeoutError / ZodError
 *
 * 足すときは、実際に投げられる型名であることを確かめてから足す（推測で広げない）。
 */

export const UNKNOWN_ERROR_KIND = 'UnknownError'

/**
 * Postgres のエラーコードは 5 桁の英数字（例: 42501 / 23505 / 42P01）。
 * 🔴 `describe-cause-chain.ts` と**同じ判定**を使う。片方だけ緩むと
 * 「画面には出るがログには出ない」「その逆」が起きて、どちらが正しいか誰も決められなくなる。
 */
export const PG_ERROR_CODE_RE = /^[0-9A-Z]{5}$/

/** cause を辿る深さの上限（`describe-cause-chain.ts` と揃える）。 */
const MAX_CAUSE_DEPTH = 5

/**
 * 例外が持つ Postgres のエラーコードを取り出す（値は一切読まない）。
 * Error だけでなく plain object も受ける（Supabase / postgrest の失敗は object で返る）。
 */
export function pgErrorCode(err: object): string | undefined {
  let code: unknown
  try {
    code = (err as { code?: unknown }).code
  } catch (_unreadableCode) {
    return undefined
  }
  return typeof code === 'string' && PG_ERROR_CODE_RE.test(code) ? code : undefined
}

const ALLOWED_ERROR_KINDS: ReadonlySet<string> = new Set([
  'Error',
  'TypeError',
  'RangeError',
  'SyntaxError',
  'ReferenceError',
  'EvalError',
  'URIError',
  'AggregateError',
  'DrizzleQueryError',
  'AbortError',
  'PostgresError',
  'TimeoutError',
  'ZodError',
])

/**
 * 例外の種類名を返す。許可リストに無い name・Error でない値・name を読めない値は 'UnknownError'。
 * 値の中身（message / stack / cause）は一切読まない。
 */
export function safeErrorKind(value: unknown): string {
  // 🔴 `instanceof` も try の中に入れる（2026-09-18・Codex MEDIUM）。
  //    `getPrototypeOf` trap が投げる Proxy を渡すと **判定そのものが throw** する。
  //    この関数は catch の中（fail-safe 経路）から呼ばれるので、ここで投げると
  //    「読めなくても画面を落とさない」という設計が丸ごと無効になる。
  try {
    if (!(value instanceof Error)) return UNKNOWN_ERROR_KIND
    const name: unknown = value.name
    return typeof name === 'string' && ALLOWED_ERROR_KINDS.has(name) ? name : UNKNOWN_ERROR_KIND
  } catch (_unreadableValue) {
    // 種類を判定できない値。中身は見ずに種類不明として扱う。
    return UNKNOWN_ERROR_KIND
  }
}

// フレーム整形の本体は v8-frame-mask.ts（行数上限で分割・入口はここからも使える）
export { NEXT_LAYER_NAMES, safeErrorFrames, safeStackFrames } from './v8-frame-mask'

/**
 * `safeErrorKind` の文字列版（台帳 CUSTOMER-PORTAL-ERROR-LOG-PII-1・2026-09-15）。
 *
 * ブラウザの `window.onerror` が渡す message は `TypeError: <本文>` の形をしている。
 * 先頭の種類名だけを許可リスト経由で取り出し、**本文は捨てる**。
 * 種類名に見えないものは `UnknownError`（保存先の message 列は NOT NULL）。
 *
 * 🔴 クライアントから来る値は信用しない。ここが信頼境界なので、
 * 「クライアント側で正規化してから送る」には頼らない（誰でも POST できる）。
 */
export function safeErrorKindFromText(text: unknown): string {
  if (typeof text !== 'string') return UNKNOWN_ERROR_KIND
  const firstLine = text.split('\n', 1)[0]?.trim() ?? ''
  const named = /^([A-Za-z_$][\w$]*)\s*:/.exec(firstLine)
  const candidate = named?.[1] ?? firstLine
  return ALLOWED_ERROR_KINDS.has(candidate) ? candidate : UNKNOWN_ERROR_KIND
}

/**
 * Postgres のエラーコードを `cause` の連鎖から取り出す（台帳 LOG-LOGGER-ERROR-OBJECT-SERIALIZE-1）。
 *
 * ## なぜ種類名だけでは足りないか
 *
 * `{kind:'PostgresError'}` だけを見ても「権限が無い(42501)」「一意制約違反(23505)」
 * 「表が無い(42P01)」のどれか分からない。**漏らさない代わりに調べられなくなる**のでは、
 * 現場は結局ログを信用せず別の場所へ値を出すようになる。
 *
 * コードは **`42501` のような固定の5桁**で、値そのものを含まない。
 * `describe-cause-chain.ts` が画面表示で同じ判断をしている（コードは出す・detail は出さない）ので、
 * 判定をそちらと1本に揃える（片方だけ緩むのを防ぐ）。
 *
 * ## 🔴 コードは cause の中にいる
 *
 * drizzle は driver の例外を `DrizzleQueryError` で包むため、**外側の例外に code は無い**。
 * 辿らないと取れない（2026-09-01 本番実測・工事タスク送信 42/42 失敗の原因特定で判明）。
 *
 * @returns 5桁の Postgres エラーコード。見つからない・Error でない場合は undefined
 */
/**
 * 応答 JSON へ入れてよい形の「例外の要約」を作る（台帳 API-RESPONSE-ERROR-MESSAGE-PASSTHROUGH-1）。
 *
 * ## なぜ要るか
 *
 * API の応答に `e instanceof Error ? e.message : String(e)` をそのまま入れている箇所があり、
 * **DB や外部 SDK の例外本文が利用者の画面まで返り得る**。Postgres の例外は
 * `Key (phone)=(090-1234-5678) already exists.` のように**値そのもの**を持つ。
 *
 * 業務用の例外クラス（`ValidationError` 等）の固定文言は人に見せるために書かれたもので、
 * そのまま返してよい。問題は **何が入っているか分からない例外** を素通しすることの方。
 *
 *     safeErrorSummary(pgErr)               // 'PostgresError(23505)'
 *     safeErrorSummary(new TypeError('x'))  // 'TypeError'
 *     safeErrorSummary('文字列')             // 'UnknownError'
 *
 * ## 🔴 本文を捨てるだけにしない
 *
 * 「漏らさない」を達成した実装が「調べられる」を落とすと、現場は結局この応答を信用せず
 * 別の場所へ値を出すようになる。**呼び出し側は `logError` / `logger.error` を必ず併せて呼び、
 * 本文は Sentry と stdout に残すこと。**応答から消すのは「誰でも見える場所」から消すためであって、
 * 調査を諦めるためではない。
 */
export function safeErrorSummary(value: unknown): string {
  const code = safeErrorCode(value)
  const kind = safeErrorKind(value)
  return code ? `${kind}(${code})` : kind
}

/**
 * ログ・Sentry へ出してよいシステムエラーコードの許可リスト（Node の errno / undici / TLS / postgres.js）。
 * 🔴 形（大文字の識別子）だけで判定しない。外部ライブラリや plain object の code には
 *    業務識別子を埋めた値も入り得る（例: ERR_09000000000・Codex HIGH）。実在する固定定数だけを通す。
 * 足すときは、実際に投げられるコードであることを確かめてから足す（推測で広げない）。
 */
const ALLOWED_SYSTEM_ERROR_CODES: ReadonlySet<string> = new Set([
  // Node errno（通信・名前解決・ファイル）
  'ECONNREFUSED',
  'ECONNRESET',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ENOTFOUND',
  'EAI_AGAIN',
  'EHOSTUNREACH',
  'ENETUNREACH',
  'EPIPE',
  'EPROTO',
  'ENOENT',
  'EACCES',
  'EPERM',
  'EADDRINUSE',
  // Node の内部エラー（fetch / stream / abort）
  'ERR_INVALID_URL',
  'ERR_MODULE_NOT_FOUND',
  'MODULE_NOT_FOUND',
  'ERR_STREAM_PREMATURE_CLOSE',
  'ERR_TLS_CERT_ALTNAME_INVALID',
  'ABORT_ERR',
  // TLS
  'CERT_HAS_EXPIRED',
  'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
  'DEPTH_ZERO_SELF_SIGNED_CERT',
  'SELF_SIGNED_CERT_IN_CHAIN',
  // undici（Node の fetch）
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_BODY_TIMEOUT',
  'UND_ERR_SOCKET',
  'UND_ERR_ABORTED',
  'UND_ERR_CLOSED',
  'UND_ERR_DESTROYED',
  'UND_ERR_INFO',
  'UND_ERR_RESPONSE_STATUS_CODE',
  // postgres.js（接続まわり）
  'CONNECTION_CLOSED',
  'CONNECTION_ENDED',
  'CONNECTION_DESTROYED',
  'CONNECT_TIMEOUT',
  // postgres.js（問い合わせを組み立てられない・Errors.generic）。
  // 実在を確認: node_modules/postgres/src/types.js:83,259（UNDEFINED_VALUE）・
  // connection.js:187（MAX_PARAMETERS_EXCEEDED）・connection.js:606（UNSAFE_TRANSACTION）。
  // lib/db/without-pipelining.ts の postgresLikeError も同じコードを投げる。
  'UNDEFINED_VALUE',
  'MAX_PARAMETERS_EXCEEDED',
  'UNSAFE_TRANSACTION',
])

/**
 * cause を辿ってシステムエラーコードを取り出す（値は一切読まない）。
 * safeErrorCode と同じく循環参照・読めない値で止まらない。
 */
export function safeSystemErrorCode(value: unknown): string | undefined {
  const seen = new Set<unknown>()
  let current: unknown = value
  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    if (typeof current !== 'object' || current === null || seen.has(current)) break
    seen.add(current)
    try {
      const code: unknown = (current as { code?: unknown }).code
      if (typeof code === 'string' && ALLOWED_SYSTEM_ERROR_CODES.has(code)) return code
      current = (current as { cause?: unknown }).cause
    } catch (_unreadable) {
      break
    }
  }
  return undefined
}

export function safeErrorCode(value: unknown): string | undefined {
  // 循環参照（err.cause === err や A→B→A）で無限ループしないよう、見た参照を覚える。
  const seen = new Set<unknown>()
  let current: unknown = value

  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    // 🔴 Error に限定しない（2026-09-15）。Supabase / postgrest の失敗は **plain object** で返り、
    //    `error?.code === '23505'` のように呼び出し側が現に code を見ている。
    //    Error だけを見ていると、そういう経路で要約が `UnknownError` に潰れて診断が死ぬ。
    //    読むのは形を固定した5桁のコードだけなので、object へ広げても値は漏れない。
    if (typeof current !== 'object' || current === null || seen.has(current)) break
    seen.add(current)
    const code = pgErrorCode(current)
    if (code) return code
    try {
      current = (current as { cause?: unknown }).cause
    } catch (_unreadableCause) {
      break
    }
  }
  return undefined
}

/**
 * Sentry（logError）へ送ってよい形に作り直した例外（AUTH-010-D 第2便・Codex BLOCKER）。
 *
 * 握って継続する経路で原因を失わないよう例外を Sentry へ送りたいが、元の例外をそのまま渡すと
 * message / stack 先頭行 / cause の本文が外部サービスへ出る。beforeSend（lib/sentry/pii.ts）は
 * メール・電話・住所の形しか伏せられず、氏名や自由文は残る。
 *
 * 作り直した例外が持つのは次だけ（どれも値を含まない）:
 *   name    … safeErrorKind（許可リストの種類名）
 *   message … safeErrorDigest（種類名＋Postgres コード／許可リストのシステムコード／timeout_<ms>ms）
 *   stack   … ヘッダ行（`種類名: message`）だけ。フレームは持たない
 * 🔴 フレームは元の stack のものも、ここで作った stack のものも持たない。形どおりのフレームでも
 *    パスに氏名・実行ユーザー名が入り得る（例: /Users/山田太郎/work/x.ts:1:1・Codex BLOCKER / HIGH）。
 *    どこで失敗したかは logger のメッセージと context で分かるようにする。
 * cause は付けない（cause 側に本文がある）。
 */
export function toSafeDiagnostic(value: unknown): Error {
  const kind = safeErrorKind(value)
  // message はログ用の要約と同じ（種類名＋Postgres コード／システムコード／timeout_<ms>ms）
  const message = safeErrorDigest(value)
  const safe = new Error(message)
  safe.name = kind
  safe.stack = `${kind}: ${message}`
  return safe
}

/**
 * withTimeout 系が投げる `<label>_timeout_<ms>ms` を `timeout_<ms>ms` に落として返す（AUTH-010-D 第2便・Codex HIGH）。
 * 本文を捨てると単なる "Error" になり、縮退の原因（タイムアウトか DB 障害か）を分けられなくなる。
 * 🔴 label は返さない。この関数は任意の例外に当てるので、外部由来の例外が同じ形
 *    （例: TaroYamada_timeout_5000ms）で氏名や識別子を運び得る。どの処理かはログのメッセージで分かる。
 * 形が完全一致しないもの・Error でない値は undefined。
 */
const TIMEOUT_MESSAGE_RE = /^[A-Za-z][A-Za-z0-9_]{0,60}_timeout_(\d{1,7})ms$/

export function safeTimeoutLabel(value: unknown): string | undefined {
  try {
    if (!(value instanceof Error)) return undefined
    const message: unknown = value.message
    if (typeof message !== 'string') return undefined
    const ms = TIMEOUT_MESSAGE_RE.exec(message)?.[1]
    return ms ? `timeout_${ms}ms` : undefined
  } catch (_unreadable) {
    return undefined
  }
}

/**
 * ログ専用の要約（AUTH-010-D 第2便・Codex HIGH）。
 *
 * safeErrorSummary は応答 JSON にも入るため文面を変えない。ログでは原因の識別子を1つ添える:
 * Postgres の5桁コード → 許可リストのシステムエラーコード → withTimeout の文言（形が完全一致するときだけ）。
 * どれも値を含まない固定の識別子。本文（message の自由文・stack・cause の本文）は含めない。
 */
export function safeErrorDigest(value: unknown): string {
  const kind = safeErrorKind(value)
  const detail = safeErrorCode(value) ?? safeSystemErrorCode(value) ?? safeTimeoutLabel(value)
  return detail ? `${kind}(${detail})` : kind
}
